import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Properties API
export const propertiesApi = {
  getAll: (status) => axios.get(`${API}/properties`, { params: { status } }),
  getOne: (id) => axios.get(`${API}/properties/${id}`),
  create: (data) => axios.post(`${API}/properties`, data),
  update: (id, data) => axios.put(`${API}/properties/${id}`, data),
  delete: (id) => axios.delete(`${API}/properties/${id}`),
};

// Documents API
export const documentsApi = {
  getAll: (propertyId) => axios.get(`${API}/documents`, { params: { property_id: propertyId } }),
  getOne: (id) => axios.get(`${API}/documents/${id}`),
  create: (data) => axios.post(`${API}/documents`, data),
  delete: (id) => axios.delete(`${API}/documents/${id}`),
};

// Key Dates API
export const keyDatesApi = {
  getAll: (propertyId, daysAhead) => axios.get(`${API}/key-dates`, { 
    params: { property_id: propertyId, days_ahead: daysAhead } 
  }),
  create: (data) => axios.post(`${API}/key-dates`, data),
  update: (id, data) => axios.put(`${API}/key-dates/${id}`, data),
  delete: (id) => axios.delete(`${API}/key-dates/${id}`),
};

// Property Expenses API
export const expensesApi = {
  getAll: (propertyId) => axios.get(`${API}/property-expenses`, { params: { property_id: propertyId } }),
  getOne: (id) => axios.get(`${API}/property-expenses/${id}`),
  create: (data) => axios.post(`${API}/property-expenses`, data),
  update: (id, data) => axios.put(`${API}/property-expenses/${id}`, data),
  delete: (id) => axios.delete(`${API}/property-expenses/${id}`),
};

// Dashboard API
export const dashboardApi = {
  get: () => axios.get(`${API}/dashboard`),
};

// Cashflow API
export const cashflowApi = {
  get: (rangeType = 'year', startDate, endDate, propertyId, propertyIds, mode = 'actual') => 
    axios.get(`${API}/cashflow`, { 
      params: { 
        range_type: rangeType,
        start_date: startDate,
        end_date: endDate,
        property_id: propertyId,
        property_ids: propertyIds ? propertyIds.join(',') : undefined,
        mode
      } 
    }),
};

// Expense Breakdown API
export const expenseBreakdownApi = {
  get: (propertyIds) => axios.get(`${API}/expense-breakdown`, {
    params: { property_ids: propertyIds ? propertyIds.join(',') : undefined }
  }),
};

// Financial Reports API (PDF Upload)
export const financialReportsApi = {
  getAll: (propertyId) => axios.get(`${API}/financial-reports`, { params: { property_id: propertyId } }),
  getOne: (id) => axios.get(`${API}/financial-reports/${id}`),
  upload: (formData) => axios.post(`${API}/financial-reports/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => axios.put(`${API}/financial-reports/${id}`, null, { params: data }),
  delete: (id) => axios.delete(`${API}/financial-reports/${id}`),
  getMonthAggregate: (propertyId, month, year) => 
    axios.get(`${API}/financial-reports/aggregate/${propertyId}/${month}/${year}`),
};

// Monthly Manual Expenses API
export const monthlyExpensesApi = {
  getAll: (propertyId, month, year) => axios.get(`${API}/monthly-expenses`, { 
    params: { property_id: propertyId, month, year } 
  }),
  create: (data) => axios.post(`${API}/monthly-expenses`, data),
  delete: (id) => axios.delete(`${API}/monthly-expenses/${id}`),
};

// Team API
export const teamApi = {
  status: () => axios.get(`${API}/team/status`),
  invite: (email) => axios.post(`${API}/team/invite`, { email }),
  members: () => axios.get(`${API}/team/members`),
  removeMember: (id) => axios.delete(`${API}/team/members/${id}`),
  pendingInvites: () => axios.get(`${API}/team/invites`),
  acceptInvite: (id) => axios.post(`${API}/team/invites/${id}/accept`),
  leave: () => axios.post(`${API}/team/leave`),
};

// Gmail API
export const gmailApi = {
  status: () => axios.get(`${API}/gmail/status`),
  connect: () => axios.get(`${API}/gmail/connect`),
  disconnect: () => axios.delete(`${API}/gmail/disconnect`),
  sync: () => axios.post(`${API}/gmail/sync`),
  importPdf: (data) => axios.post(`${API}/gmail/import`, data),
  dismiss: (data) => axios.post(`${API}/gmail/dismiss`, data),
  imports: () => axios.get(`${API}/gmail/imports`),
};

// Tax Report API
export const taxReportApi = {
  get: (taxYear, periodType = 'full_year', quarter, propertyIds) => 
    axios.get(`${API}/tax-report`, {
      params: {
        tax_year: taxYear,
        period_type: periodType,
        quarter: quarter,
        property_ids: propertyIds ? propertyIds.join(',') : undefined
      }
    }),
};

// Utility functions
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(amount);
};

export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const getDaysUntil = (dateString) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(dateString);
  targetDate.setHours(0, 0, 0, 0);
  const diffTime = targetDate - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const getUrgencyLevel = (dateString) => {
  const days = getDaysUntil(dateString);
  if (days < 0) return 'overdue';
  if (days <= 30) return 'critical';
  if (days <= 60) return 'warning';
  return 'safe';
};

export const DOC_TYPES = [
  { value: 'letting_statement', label: 'Letting Statement' },
  { value: 'inspection_report', label: 'Inspection Report' },
  { value: 'insurance', label: 'Insurance Document' },
  { value: 'mortgage', label: 'Mortgage Document' },
  { value: 'other', label: 'Other' },
];

// Document folder categories (replacing type-based organization)
export const DOC_FOLDERS = [
  { value: 'landlord_reports', label: 'Landlord Reports' },
  { value: 'tenancy_agreements', label: 'Tenancy Agreements' },
  { value: 'finance_mortgage', label: 'Finance / Mortgage Documents' },
  { value: 'other', label: 'Other' },
];

// Map old doc_type values to new folder values
export const mapDocTypeToFolder = (docType) => {
  const mapping = {
    'letting_statement': 'landlord_reports',
    'insurance': 'finance_mortgage',
    'mortgage': 'finance_mortgage',
    'inspection_report': 'other',
    'other': 'other',
  };
  return mapping[docType] || 'other';
};

// Get folder label from value
export const getFolderLabel = (folder) => {
  const found = DOC_FOLDERS.find(f => f.value === folder);
  return found ? found.label : 'Other';
};

export const DATE_TYPES = [
  { value: 'mortgage_payment', label: 'Mortgage Payment Date' },
  { value: 'refinancing', label: 'Refinancing Opportunity (Fixed-rate Expiry)' },
  { value: 'insurance_renewal', label: 'Insurance Renewal' },
  { value: 'tax_deadline', label: 'Self-Assessment Tax Deadline' },
  { value: 'tenancy_renewal', label: 'Tenancy End / Renewal' },
  { value: 'rent_tracking', label: 'Rent Payment Tracking' },
  { value: 'other', label: 'Other' },
];

export const EXPENSE_CATEGORIES = [
  { value: 'mortgage', label: 'Finance (Mortgage Payment)' },
  { value: 'council_tax', label: 'Council Tax' },
  { value: 'letting_agent_fee', label: 'Letting Agent Fee' },
  { value: 'insurance', label: 'Landlord Insurance' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'other', label: 'Other' },
];

// Manual expense categories for monthly entries
export const MANUAL_EXPENSE_CATEGORIES = [
  { value: 'repairs_maintenance', label: 'Repairs & Maintenance' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'ground_rent', label: 'Ground Rent / Service Charge' },
  { value: 'accountancy_legal', label: 'Accountancy / Legal Fees' },
  { value: 'furnishings', label: 'Furnishings' },
  { value: 'other', label: 'Other' },
];

export const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export const getCategoryLabel = (category) => {
  const allCategories = [...EXPENSE_CATEGORIES, ...MANUAL_EXPENSE_CATEGORIES];
  const found = allCategories.find(c => c.value === category);
  return found ? found.label : category;
};
